import React, { useContext, useState, useEffect } from 'react';
import {
   Button, ButtonGroup, Card, CardBody, CardTitle, Col, Collapse,
   Input, Modal, ModalBody, ModalFooter, ModalHeader, Row, Table
} from 'reactstrap';
import { useNavigate } from 'react-router-dom';
import { confirmAlert } from "react-confirm-alert";
import { toast } from "react-toastify";
import BarcodeScannerComponent from "react-qr-barcode-scanner";
import { ServiceThreadSpinner } from '.';
import { DesktopContainer } from '../layouts';
import { api, constants, storage, UserContext } from '../utils';
import _ from 'lodash'; 
import { AiFillEye, AiFillEyeInvisible } from "react-icons/ai";
import { MdArrowRight, MdOutlineExpandLess, MdOutlineExpandMore } from "react-icons/md";

export default function AddInventory() {
   const ICONS = {
      openEye: <AiFillEye className="mobileEyeToggle align-self-center" />,
      shutEye: <AiFillEyeInvisible className="mobileEyeToggle align-self-center" />,
      collapseIcon: <MdOutlineExpandLess className="mobileToggle align-self-center" />,
      expandIcon: <MdOutlineExpandMore className="mobileToggle align-self-center" />,
      arrowIcon: <MdArrowRight className="mobileToggleEx align-self-center" />
   }
   const navigate = useNavigate();
   const { currentUser } = useContext(UserContext);
   const [allowZeroTare, setZeroTare] = useState(false);
   const [barcode, setBarcode] = useState(null);
   const [errorMsg, setErrorMsg] = useState(null);
   const [expanded, setExpanded] = useState(null);
   const [hdrText, setHdr] = useState("To add a production item into inventory, scan a WO label.");
   const [isCase, setIsCase] = useState(true);
   const [loaded, setLoaded] = useState(false);
   const [manualEntry, setManual] = useState(null);
   const [model, setModel] = useState({});
   // Display only: the server's note that this PO is being received on the new /m app.
   const [newScreenNote, setNewScreenNote] = useState(null);
   const [open, setOpen] = useState(null);
   const [override, setOverride] = useState(null);
   const [overrideRcpt, setOverrideRcpt] = useState(null);
   const [pass, setPass] = useState("");
   const [passwordType, setPasswordType] = useState("password");
   const [rcpt, setRcpt] = useState({});
   const [review, setReview] = useState(false);
   const [spin, setSpinner] = useState(false);
   const [weight, setWgt] = useState(false);
   const [wgtText, setWgtTxt] = useState("LBS");
   const sleep = ms => new Promise(r => setTimeout(r, ms));
   let modalClassName = currentUser.theme ? `${currentUser.theme}` : "lightTheme";
   let savedDevice = JSON.parse(storage.getItem("deviceType"));
   const isMobile = savedDevice.isMobile;
   const hasScanner = savedDevice.hasScanner;
   const canEdit = currentUser.isAdmin || currentUser.isPowerUser || currentUser.isWarehouseEdit;


   useEffect(() => {
      if (currentUser.isAdmin || currentUser.isPowerUser || currentUser.isWarehouseEdit) {
         // These roles are allowed to do receiving as well as adding a case to inventory
         setHdr("To add a production item into inventory, scan a WO label. Scan an IV label to start receiving.");

         // Check if we failed to complete a receipt in process. Either set up to complete or initialize a new rcpt.
         let lastRcpt = JSON.parse(storage.getItem("rcptInProcess"));
         if (lastRcpt && lastRcpt.poNumber) {
            confirmAlert({
               title: "Unfinished PO Receipt",
               message: `A previous receipt for ${lastRcpt.poNumber} was not finished. Do you want to reload it?`,
               buttons: [
                  {
                     label: "Yes",
                     onClick: () => { setRcpt(lastRcpt); },
                  },
                  {
                     label: "No",
                     onClick: () => { doInit(); }
                  },
               ],
            });
         }
         else {
            doInit();
         }
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [])

   useEffect(() => {
      async function load() {
         await sleep(80);  // allows time for input to be recognized for focus
         if (!loaded)
            inputFocus("searchInput");         
         else if (isCase)
            inputFocus("gross");
      }
      load();
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [loaded])

   useEffect(() => {
      // Is current barcode a production WO case label or an IV transfer label?
      if (barcode) {
         // QR codes will start with B for Bulk IP case labels and with C for FG and non-bulk IP labels
         let tmp = barcode.toUpperCase().substring(0, 1);
         setIsCase(tmp === "B" || tmp === "C" || !isNaN(tmp));
      }
      // if device has built-in scanner see if a scan was completed
      if (hasScanner && barcode && !manualEntry) {
         setLoaded(false);
         getCase();
      }
      if (manualEntry)
         inputFocus("manualInput");
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [barcode])

   useEffect(() => {
      async function chooseNext() {
         await sleep(50); // allows time for input to be recognized for focus
         if (override === null) {
            if (loaded) {
               inputFocus("gross");
            }
            return;
         }
         if (allowZeroTare) {
            setOverride(null);
            doReview();
         }
      }
      chooseNext();
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [allowZeroTare])

   useEffect(() => {
      if (manualEntry) {
         inputFocus("manualInput");
         return;
      }
      if (!barcode) {
         inputFocus("searchInput");
         return;
      }

      setLoaded(false);
      getCase();
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [manualEntry])

   useEffect(() => {
      // Enter receiving weight
      if (weight) {
         inputFocus("wgt");
         return;
      }
      inputFocus("searchInput");
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [weight])

   function addCase() {
      // Add a receipt case to a po receiving line item
      setSpinner(true);
      api
         .post(`Mobile/AddReceiptCase`, rcpt)
         .then(response => {
            if (response.data.success) {
               setRcpt(response.data.rcpt);
               storage.setItem("rcptInProcess", JSON.stringify(response.data.rcpt));
               setWgt(false);
               setWgtTxt("LBS");
            }
            else {
               toast.error(response.data.message);
               inputFocus("wgt");
            }
         })
         .catch((error) => {
            api.catchHandler(error);
            toast.error(error);
            inputFocus("wgt");
         })
         .finally(() => {
            setSpinner(false);
         });
   }

   async function cancelReview() {
      // Cancel review of a production WO case
      await sleep(50); // allows time for input to be recognized for focus
      setReview(false);
      inputFocus("gross");
   }

   function changeLabelWeight() {
      if (canEdit) {
         var nav = constants.ENDPOINTS.labelWeight;
         navigate(nav);
      }
   }

   function clearCase() {
      // Clear the working case to process on po receipt
      if (rcpt?.length > 0) {
         let changes = Object.assign({}, rcpt);
         changes.workCase["barcodeId"] = 0;
         changes.workCase["caseId"] = 0;
         changes.workCase["itemNumber"] = "N/A";
         changes.workCase["weight"] = 0;
         changes.workCase["weightInKg"] = false;
         setRcpt(changes);
         storage.setItem("rcptInProcess", JSON.stringify(changes));

         setWgt(false);
         setWgtTxt("LBS");
      }
      inputFocus("searchInput");
   }

   function doInit() {
      // Initialize a blank receiving model
      setNewScreenNote(null);
      setSpinner(true);
      api
         .fetch(`Mobile/InitReceiving`)
         .then(response => {
            if (response.data.success) {
               setRcpt(response.data.rcpt);
               storage.setItem("rcptInProcess", JSON.stringify(response.data.rcpt));
            }
            else {
               toast.error(response.data.message);
               storage.setItem("rcptInProcess", JSON.stringify({}));
            }
         })
         .catch((error) => {
            storage.setItem("rcptInProcess", JSON.stringify({}));
            api.catchHandler(error);
         })
         .finally(() => {
            setSpinner(false);
            inputFocus("searchInput");
         })
   }

   async function doReview() {
      // Review the production WO case being added to inventory
      await sleep(50);  // allows time for input to be recognized for focus

      if (!model.grossWeight || model.grossWeight <= 0) {
         toast.error("Gross weight must be greater than zero");
         inputFocus("gross");
         return;
      }

      if (model.showQty && (!model.caseQty || model.caseQty <= 0)) {
         toast.error("Case quantity must be provided");
         inputFocus("qty");
         return;
      }

      if (model.maxWeight !== 0) {
         var netWgt = (model.tareWeight > 0) ? model.grossWeight - model.tareWeight : model.grossWeight;
         if ((netWgt > model.maxWeight || netWgt < model.minWeight) && !model.overQty) {
            var msg = 'Case NET weight has an expected weight of ' + model.expectedWeight.toLocaleString("en-US", { maximumFractionDigits: 0 }) + ' lbs, and must be between ' + model.minWeight.toLocaleString("en-US", { maximumFractionDigits: 0 }) + ' and ' + model.maxWeight.toLocaleString("en-US", { maximumFractionDigits: 0 }) + ' lbs. To override this weight, check the override case weight check box.';
            toast.error(msg);
            inputFocus("gross");
            return;
         }
         model.netWeight = netWgt;
      }

      // A zero tare weight is allowed for any item with a class of BOND MIX or when the gross weight is < 10 lbs
      // If these conditions are not met a supervisor must override this to continue.
      if (model.tareWeight <= 0 && !allowZeroTare && (model.grossWeight >= 10 || model.itemClass !== 'BOND MIX')) {
         setOverride(true);
         inputFocus("pswd");
         return;
      }

      setReview(true);
   }

   function getCase() {
      if (barcode) {
         // If a QR code was scanned then parse out just the barcode from the data
         var code = constants.GetRealBarcode(barcode);
         if (code.length < 2) return;

         if (code.substring(0, 2) === "IV") {
            // process IV transfer label barcode
            if (currentUser.isAdmin || currentUser.isPowerUser || currentUser.isWarehouseEdit) {
               setIsCase(false);
               setSpinner(true);
               processBarcode(code);
            }
            else {
               toast.error("You are not authorized to do any receiving");
               setIsCase(true);
               storage.setItem("rcptInProcess", JSON.stringify({}));
               setRcpt({});
            }
         }
         else if (!loaded) {
            // clear any previous receipt in process
            storage.setItem("rcptInProcess", JSON.stringify({}));
            setRcpt({});
            // process production WO case label barcode
            setIsCase(true);
            setSpinner(true);
            api
               .post(`Mobile/GetCaseByBarcode/${code}`)
               .then(response => {
                  if (response.data.success) {
                     setModel(response.data.model);
                     setLoaded(true);
                  }
                  else {
                     toast.error(response.data.message);
                     setLoaded(false);
                  }
               })
               .catch((error) => {
                  api.catchHandler(error);
                  toast.error(error);
                  setLoaded(false);
               })
               .finally(() => {
                  setBarcode(null);
                  setSpinner(false);
               });            
         }
      }
   }

   function getObjectByKey(array, key, value) {
      // Return the index of the key/value pair
      for (var i = 0; i < array.length; i++) {
         if (array[i][key] === value) {
            return i;
         }
      }
      return null;
   }

   const handleFocus = (event) => event.target.select();

   const handleKeypress = e => {
      // On key press for search input field
      if (e.charCode === 13 || e.key === 'Enter') {
         setManual(null);
         return;
      }
      inputFocus("manualInput");
   };

   async function inputFocus(fieldname) {
      await sleep(80);  // allows time for input to be recognized
      const el = document.querySelector(`input[name=${fieldname}]`);
      if (el !== null) {
         await el.focus();
      }
   }

   function isDecimalVal(str) {
      // One to five digits + optional decimal up to 4 places
      return (/^[0-9]{1,5}(\.[0-9]{0,4})?$/).test(str);
   }

   function isValidOverride() {
      // Override production WO Case Zero Tare Weight
      if (pass.length > 0) {
         setSpinner(true);
         api
            .post("Mobile/AllowCaseOverride", { password: pass })
            .then(response => {
               if (response.data.success) {
                  onChange("allowZeroTare", true); // pass to api
                  setErrorMsg(null);  // remove any error msg
                  setZeroTare(true);  // allow a zero tare weight
               }
               else {
                  setErrorMsg(response.data.message);
                  toast.error(response.data.message);
                  setZeroTare(false);
                  inputFocus("pswd");
               }
            })
            .catch((error) => {
               api.catchHandler(error);
               toast.error(error);
               setErrorMsg(error);
               setZeroTare(false);
               inputFocus("pswd");
            })
            .finally(() => {
               setPasswordType("password");
               setPass("");
               setSpinner(false);
            });
      }
   }

   function isValidRcptOverride() {
      // Override total line item weight not matching expected weight
      if (pass.length > 0) {
         setSpinner(true);
         api
            .post("Mobile/AllowReceiptOverride", { password: pass })
            .then(response => {
               if (response.data.success) {
                  postAdminReceipt();
               }
               else {
                  toast.error(response.data.message);
                  inputFocus("pswdRcpt");
               }
            })
            .catch((error) => {
               api.catchHandler(error);
               toast.error(error);
               inputFocus("pswdRcpt");
            })
            .finally(() => {
               setPasswordType("password");
               setPass("");
               setSpinner(false);
            });
      }
   }

   async function lookup(barcode) {
      // On change of search input field
      setBarcode(barcode);

      if (manualEntry) {
         inputFocus("manualInput");
         return;
      }
      inputFocus("searchInput");
   }

   function onCaseChange(field, value) {
      let changes = Object.assign({}, rcpt);
      if (field === "weight") {
         // set weight for current label
         let parsedValue = parseFloat(value);
         if (value === undefined || value === "" || !isDecimalVal(value) || parsedValue < 0) {
            let parsedField = parseFloat(changes.workCase[field]);
            if (isNaN(parsedField) || parsedField < 0 || !isDecimalVal(changes.workCase[field]))
               changes.workCase[field] = "0"; // Existing value wasn't good either so reset
            else
               changes.workCase[field] = parsedField; // Existing value is good, just keep it
            toast.info("Net Weight must be numeric with no more than 4 decimals");
         }
         else {
            let isKg = wgtText === "Kg";
            let kgValue = isKg ? constants.GetPoundsFromKgs(parsedValue) : parsedValue;
            if (kgValue > 25000) {
               toast.warning("Net Weight cannot exceed 25,000 lbs or 11,339.8227 kgs");
               changes.workCase[field] = isKg ? "11339.8227" : "25000";
            }
            else {
               changes.workCase[field] = value;
            }
         }
      }
      else {
         // set weightInKg true or false for current label
         changes.workCase[field] = value;
      }

      // Changes to the po receipt case in process
      setRcpt(changes);
      storage.setItem("rcptInProcess", JSON.stringify(changes));
   }

   function onChange(field, value) {
      // Changes to the production WO case in process
      let changes = Object.assign({}, model);
      changes[field] = value;
      setModel(changes);
   }

   function postAdminReceipt() {
      setSpinner(true);
      api
         .post(`Mobile/PostAdminReceipt`, rcpt)
         .then(response => {
            if (response.data.success) {
               toast.success("PO posted successfully");
               storage.setItem("rcptInProcess", JSON.stringify({}));
               navigate(constants.ENDPOINTS.mobileHome);
            }
            else {
               toast.error(response.data.message);
            }
         })
         .catch((error) => {
            api.catchHandler(error);
         })
         .finally(() => {
            setSpinner(false);
            inputFocus("searchInput");
         });
   }

   function postReceipt() {
      setSpinner(true);
      api
         .post(`Mobile/PostReceipt`, rcpt)
         .then(response => {
            if (response.data.success) {
               toast.success("PO posted successfully");
               storage.setItem("rcptInProcess", JSON.stringify({}));
               navigate(constants.ENDPOINTS.mobileHome);
            }
            else {
               setSpinner(false);
               if (response.data.message.indexOf('A recent attempt to post this PO') >= 0) {
                  toast.error('Possible Duplicate Receipt: ' + response.data.message);
                  inputFocus("searchInput");
               }
               else if (response.data.message.indexOf('The purchase order has been Received, Canceled, or Closed') >= 0) {
                  toast.error('The purchase order has already been Received, Canceled, or Closed. Clearing this PO from the screen.');
                  doInit();
               }
               else if (response.data.message.indexOf(constants.PO_RECEIPT_POST_FAULT) >= 0) {
                  // A system fault, not a data problem - GP may already hold the receipt, so never offer the override post.
                  toast.error(response.data.message);
                  inputFocus("searchInput");
               }
               else if (response.data.message.indexOf(constants.PO_RECEIPT_IN_PROGRESS) >= 0) {
                  // Another post of this PO is running - not an error to override; try again in a moment.
                  toast.error(response.data.message);
                  inputFocus("searchInput");
               }
               else {
                  confirmAlert({
                     title: "This PO contains errors. Override?",
                     message: response.data.message + " Do you still want to post this PO? Click yes to enter supervisor password. Click no to go back and correct mistakes.",
                     buttons: [
                        {
                           label: "Yes",
                           onClick: () => {
                              setOverrideRcpt(true);
                              inputFocus("pswd");
                           }
                        },
                        {
                           label: "No",
                           onClick: () => {
                              setOverrideRcpt(null);
                              inputFocus("searchInput");
                           },
                        },
                     ],
                  });
               }
            }
         })
         .catch((error) => {
            api.catchHandler(error);
            inputFocus("searchInput");
         })
   }

   async function processBarcode(code) {
      // Process the IV transfer Label barcode for receiving
      let reciept = Object.assign({}, rcpt);
      reciept.barcode = code;

      api
         .post(`Mobile/GetReceiptCase/`, reciept)
         .then(response => {
            if (response.data.success) {
               setRcpt(response.data.rcpt);
               storage.setItem("rcptInProcess", JSON.stringify(response.data.rcpt));
               setNewScreenNote(response.data.newScreenNote);
               setWgt(true);
               setLoaded(true);
            }
            else {
               toast.error(response.data.message);
               setLoaded(false);
            }
         })
         .catch((error) => {
            api.catchHandler(error);
            toast.error(error);
            setLoaded(false);
         })
         .finally(() => {
            setBarcode(null);
            setSpinner(false);
         });
   }

   function removeCase(item, id) {
      // Remove a specific case from a po receipt line item
      confirmAlert({
         title: "Please Confirm",
         message: "Are you sure you want to remove this case?",
         buttons: [
            {
               label: "Yes",
               onClick: () => {
                  inputFocus("searchInput");
                  // find out which item in the array is the one containing the case we want to remove
                  let changes = Object.assign({}, rcpt);
                  var itmIdx = getObjectByKey(changes.lineItems, 'itemNumber', item);
                  if (itmIdx > -1) {
                     // find out which case in the array is the one we want to remove
                     var caseIdx = getObjectByKey(changes.lineItems[itmIdx].cases, 'caseId', id);
                     if (caseIdx > -1) {
                        changes.lineItems[itmIdx].cases.splice(caseIdx, 1);
                     }

                     // update the total weight for this item and/or remove it if only case
                     var currWgt = changes.lineItems[itmIdx].cases.reduce((accum, item) => accum + item.weight, 0)
                     changes.lineItems[itmIdx].currentWeight = currWgt;
                     if (currWgt <= 0) {
                        changes.lineItems.splice(itmIdx, 1);
                        if (changes.lineItems.length === 0) {
                           changes.poNumber = "";
                        }
                     }
                     setRcpt(changes);
                     storage.setItem("rcptInProcess", JSON.stringify(changes));
                  }
               },
            },
            {
               label: "No",
               onClick: () => {
                  inputFocus("searchInput");  // make sure this is what we need to do
               },
            },
         ],
      });
   }

   function removeItem(item) {
      // Remove a po line item and all its associated cases from a receipt
      confirmAlert({
         title: "Please Confirm",
         message: "Are you sure you want to remove this item? All scanned cases for this item will also be removed.",
         buttons: [
            {
               label: "Yes",
               onClick: () => {
                  inputFocus("searchInput");
                  // find out which item in the array is the one we want to remove
                  let changes = Object.assign({}, rcpt);
                  var itmIdx = getObjectByKey(changes.lineItems, 'itemNumber', item);
                  if (itmIdx > -1) {
                     changes.lineItems.splice(itmIdx, 1);
                     if (changes.lineItems.length === 0) {
                        changes.poNumber = "";
                     }
                     setRcpt(changes);
                     storage.setItem("rcptInProcess", JSON.stringify(changes));
                  }
               },
            },
            {
               label: "No",
               onClick: () => {
                  inputFocus("searchInput");  // make sure this is what we need to do
               },
            },
         ],
      });
   }

   function reset() {
      setModel({});          // clear any previous production case model
      setBarcode(null);      // clear any barcode previously scanned
      setErrorMsg(null);     // clear supervisor override error message
      setIsCase(true);       // default label type is production case
      setLoaded(false);      // starting over, must scan or enter a barcode
      setOpen(null);         // hide barcode scanner dialog
      setOverride(null);     // hide zero tare override dialog
      setOverrideRcpt(null); // hide po weight override dialog
      setPass("");           // clear this
      setPasswordType("password");
      setReview(false);      // hide review segment
      setSpinner(false);     // hide spinner
      setZeroTare(false);    // do not allow a zero tare weight
   }

   const toggle = (idx) => {
      // Expand or collapse the receiving segment
      if (!expanded || idx !== expanded) {
         setExpanded(idx);
      } else if (expanded === idx) {
         setExpanded(null);
      }
      inputFocus("searchInput");
   }

   function toggleKg() {
      // Is the po receipt line item case weight being entered in Kgs or Lbs?
      if (wgtText === "LBS") {
         setWgtTxt("Kg");
         onCaseChange("weightInKg", true);
      }
      else {
         setWgtTxt("LBS");
         onCaseChange("weightInKg", false);
      }
   }

   const togglePassword = () => {
      // see the password yes/no ?
      if (passwordType === "password") {
         setPasswordType("text")
         return;
      }
      setPasswordType("password")
   }

   function toggleQtyCheck() {
      let changes = Object.assign({}, model);
      changes["overQty"] = !model.overQty;
      setModel(changes);
   }

   function tryAddCase() {
      // Add production WO case to inventory
      setSpinner(true);
      api
         .post(`Mobile/AddCaseToInventory`, model)
         .then(response => {
            if (response.data.success) {
               toast.success('Case was successfully added to inventory');
            }
            else {
               toast.error(response.data.message);
            }
         })
         .catch((error) => {
            api.catchHandler(error);
            toast.error(error);
         })
         .finally(() => {
            reset();
         });
   }

   return (
      <>
         <DesktopContainer screenName="Inventory Entry">
            <ServiceThreadSpinner on={spin}>
               {!loaded || (!isCase && rcpt)?
                  <div>
                     <Row><Col className="h6 fw-bold" xs="12">{hdrText}</Col></Row>
                     <Row><Col><hr className="mb-0 my-1" /></Col></Row>
                     <br />
                     {/* Change Label Weight Button */}
                     {canEdit ?
                        <div>
                           <Row className="justify-content-center">
                              <Col xs="12">
                                 <Button title="label weight" className="shredButton w-100 btn-md" onClick={() => changeLabelWeight()}>
                                    Change Label Weight
                                 </Button>
                              </Col>
                           </Row>
                           <Row className="lineheight8">&nbsp;</Row>
                        </div>
                        : null
                     }

                     {/* Scan Button */}
                     {!hasScanner && isMobile ?  //  && rcpt ?
                        <div>
                           <Row className="justify-content-center">
                              <Col xs="12">
                                 <Button title="scan entry" className="shredButton w-100 btn-md" onClick={() => setOpen(1)}>
                                    Scan Barcode
                                 </Button>
                              </Col>
                           </Row>
                           <Row className="lineheight8">&nbsp;</Row>
                        </div>
                        : null
                     }

                     <div>
                        {/* Manual Entry Button */}
                        <Row className="justify-content-center">
                           <Col xs="12">
                              <Button title="manual entry" className="shredButton w-100 btn-md" onClick={() => setManual(1)}>
                                 Manual Entry
                              </Button>
                           </Col>
                        </Row>
                        {/* Built-in Scanner Barcode */}
                        {isMobile ?
                           <div>
                              <Row className="lineheight8">&nbsp;</Row>
                              <Row className="justify-content-center">
                                 <Col xs="12">
                                    <Input
                                       name="searchInput"
                                       placeholder="Focus here to scan barcode"
                                       className="h-100"
                                       onChange={(e) => lookup(e.target.value.toUpperCase())}
                                    />
                                 </Col>
                              </Row>
                           </div>
                           : null
                        }
                     </div>
                  </div>
                  : null
               }
               <Row><Col><hr className="my-0" /></Col></Row>
               <br />


               {/* Enter Weights for a Finished Good Case Before Review */}
               {loaded && !review && isCase ?
                  <div>
                     <Card className="mobileBlueBgAlt text-white">
                        <CardBody className="mobileBlueBgAlt text-white">
                           <CardTitle style={{ textAlign: "center" }} className="mobileBlueBg text-white my-0 py-0 px-0">Enter Case Weight</CardTitle>
                           <Row><Col><hr className="my-0 text-white" /></Col></Row>
                           <Table borderless size="sm">
                              <tbody>
                                 <tr>
                                    <td className="data-cell my-0 py-0" width="85px">
                                       <label className="text-black h6 fw-bold my-0">Item:</label>
                                    </td>
                                    <td className="data-cell my-0 py-0">
                                       <label className="text-black h6 mx-1 my-0">{model.itemWithDesc}</label>
                                    </td>
                                 </tr>
                                 <tr>
                                    <td className="data-cell my-0 py-0">
                                       <label className="text-black h6 fw-bold my-0">Case:</label>
                                    </td>
                                    <td className="data-cell my-0 py-0">
                                       <label className="text-black h6 mx-1 my-0">{model.caseDisplayId}</label>
                                    </td>
                                 </tr>
                                 <tr>
                                    <td className="data-cell my-0 py-0">
                                       <label className="text-black h6 fw-bold my-0">Gross Wt:</label>
                                    </td>
                                    <td className="data-cell my-0 py-0">
                                       <Input
                                          name="gross"
                                          type="number"
                                          className="h-100 py-1"
                                          value={model.grossWeight}
                                          max={9999}
                                          min={1}
                                          required
                                          onFocus={handleFocus}
                                          onChange={(e) => { onChange("grossWeight", e.target.value) }}
                                       />
                                    </td>
                                 </tr>
                                 <tr>
                                    <td className="data-cell my-0 py-0">
                                       <label className="text-black h6 fw-bold my-0">Tare Wt:</label>
                                    </td>
                                    <td className="data-cell my-0 py-0">
                                       <Input
                                          type="number"
                                          className="h-100 py-1"
                                          value={model.tareWeight}
                                          required
                                          onFocus={handleFocus}
                                          onChange={(e) => { onChange("tareWeight", e.target.value) }}
                                       />
                                    </td>
                                 </tr>
                                 <tr>
                                    <td colSpan="2" className="data-cell my-0 py-0">
                                       <label className="text-black"><input type="checkbox" checked={model.overQty} className="me-2" onChange={() => toggleQtyCheck()} />Override Case Weight</label>
                                    </td>
                                 </tr>
                                 {model.showQty ?
                                    <tr>
                                       <td className="data-cell my-0 py-0">
                                          <label className="text-black h6 fw-bold my-0">Case Qty:</label>
                                       </td>
                                       <td className="data-cell my-0 py-0">
                                          <Input
                                             name="qty"
                                             type="number"
                                             className="h-100"
                                             value={model.caseQty}
                                             min={1}
                                             required
                                             onFocus={handleFocus}
                                             onChange={(e) => { onChange("caseQty", e.target.value) }}
                                          />
                                       </td>
                                    </tr>
                                    : null
                                 }
                              </tbody>
                           </Table>
                        </CardBody>
                     </Card>
                     <br />
                     <div>
                        <Row><Col><hr className="my-0" /></Col></Row>
                        {model.grossWeight > 0 ?
                           <Row>
                              <Col xs="12">
                                 <Button title="Save" className="shredButton w-100 btn-lg my-3" onClick={() => doReview()}>
                                    OK
                                 </Button>
                              </Col>
                           </Row>
                           : null
                        }
                        <Row>
                           <Col xs="12">
                              <Button title="Cancel" className="btn w-100 btn-lg my-3" onClick={() => reset()}>
                                 Cancel
                              </Button>
                           </Col>
                        </Row>
                     </div>
                  </div>
                  : null
               }


               {/* Review Finished Good Case Before Adding to Inventory */}
               {review && isCase ?
                  <div>
                     <Card className="mobileBlueBgAlt py-0">
                        <CardBody className="mobileBlueBgAlt">
                           <CardTitle style={{ textAlign: "center" }} className="mobileBlueBg text-white my-0 py-0 px-0">Review Case Information</CardTitle>
                           <Row className="text-white mx-1" style={{ textAlign: "center" }}>Please review the case information! Once a case has been added it cannot be changed.</Row>
                           <Row><Col><hr className="text-white my-1" /></Col></Row>

                           <Table borderless size="sm">
                              <tbody>
                                 <tr>
                                    <td className="data-cell my-0 py-0" width="65px">
                                       <label className="text-black h6 fw-bold my-0">Item:</label>
                                    </td>
                                    <td className="data-cell my-0 py-0">
                                       <label className="text-black h6 mx-1 my-0">{model.itemWithDesc}</label>
                                    </td>
                                 </tr>
                                 <tr>
                                    <td className="data-cell my-0 py-0">
                                       <label className="text-black h6 fw-bold my-0">Case:</label>
                                    </td>
                                    <td className="data-cell my-0 py-0">
                                       <label className="text-black h6 mx-1 my-0">{model.caseDisplayId}</label>
                                    </td>
                                 </tr>
                                 <tr>
                                    <td className="data-cell my-0 py-0">
                                       <label className="text-black h6 fw-bold my-0">Qty:</label>
                                    </td>
                                    <td className="data-cell my-0 py-0">
                                       <label className={(!model.caseQty || model.caseQty <= 0) ? "text-red h6 mx-1 my-0" : "text-black h6 mx-1 my-0"}>{model.caseQty && model.caseQty > 0 ? model.caseQty : "N/A"}</label>
                                    </td>
                                 </tr>
                                 <tr>
                                    <td className="data-cell my-0 py-0">
                                       <label className="text-black h6 fw-bold my-0">Gross:</label>
                                    </td>
                                    <td className="data-cell my-0 py-0">
                                       <label className="text-black h6 mx-1 my-0">{model.grossWeight}</label>
                                    </td>
                                 </tr>
                                 <tr>
                                    <td className="data-cell my-0 py-0">
                                       <label className="text-black h6 fw-bold my-0">Net:</label>
                                    </td>
                                    <td className="data-cell my-0 py-0">
                                       <label className="text-black h6 mx-1 my-0">{model.netWeight}</label>
                                    </td>
                                 </tr>
                                 <tr>
                                    <td className="data-cell my-0 py-0">
                                       <label className="text-black h6 fw-bold my-0">Tare:</label>
                                    </td>
                                    <td className="data-cell my-0 py-0">
                                       <label className="text-black h6 mx-1 my-0">{model.tareWeight}</label>
                                    </td>
                                 </tr>
                              </tbody>
                           </Table>
                           <Row><Col><hr className="text-white my-1" /></Col></Row>
                           <Row>
                              <Col xs="12">
                                 <Button className="shredButton w-100 btn-md my-1" onClick={() => tryAddCase()}>
                                    Add Case
                                 </Button>
                              </Col>
                           </Row>
                           <Row>
                              <Col xs="12">
                                 <Button className="btn w-100 btn-md my-2" onClick={() => cancelReview()}>
                                    Edit Case
                                 </Button>
                              </Col>
                           </Row>
                        </CardBody>
                     </Card>
                  </div>
                  : null
               }


               {/* PO Receiving */}
               {rcpt?.lineItems && rcpt.lineItems.length > 0 ?
                  <div>
                     <Row><Col><hr className="my-0" /></Col></Row>
                     <Row className="lineheight8">&nbsp;</Row>
                     <Row><Col className="h5" xs="12">{rcpt.poNumber}</Col></Row>
                     {newScreenNote ? <Row><Col xs="12"><small>{newScreenNote}</small></Col></Row> : null}
                     {_.map(rcpt.lineItems, (item, index) => {
                        return (
                           <div details={item} key={index} >
                              <Card id="toggler" className="py-0" onClick={() => toggle(index)} style={{ cursor: "pointer" }}>
                                 <CardBody className={(index % 2 === 0) ? "mobileBlueBg text-white my-0 py-2 px-2" : "mobileBlueBgAlt text-white my-0 py-2 px-2"}>
                                    <CardTitle className={(index % 2 === 0) ? "mobileBlueBg text-white my-0 py-0 px-0" : "mobileBlueBgAlt text-white my-0 py-0 px-0"}>
                                       {(expanded === index) ? ICONS.collapseIcon : ICONS.expandIcon} {item.itemNumber}&nbsp;&nbsp;
                                       <Button children="Remove Item" className="btn btn-sm" onClick={() => {
                                          removeItem(item.itemNumber);
                                       }} /><br />Total: {item.currentWeight} of {item.qtyToReceive} lbs
                                    </CardTitle>
                                    {item.cases && item.cases.length > 0 ?
                                       <Row>
                                          <Col xs="12">
                                             <Collapse className="text-white mx-0 py-0 px-0" toggler="#toggler" isOpen={expanded === index}>
                                                {_.map(item.cases, (lot, idx) => {
                                                   return (
                                                      <div details={lot} key={idx}>
                                                         {ICONS.arrowIcon} {lot.nodeDesc}&nbsp;&nbsp;
                                                         <Button children="Remove Case" className="btn btn-sm" onClick={() => {
                                                            removeCase(lot.itemNumber, lot.caseId);
                                                         }} />
                                                      </div>
                                                   )
                                                })}
                                             </Collapse>
                                          </Col>
                                       </Row>
                                       : null
                                    }
                                 </CardBody>
                              </Card>
                           </div>
                        )
                     })}
                     <Row className="lineheight8">&nbsp;</Row>
                     <Row className="justify-content-center">
                        <Col xs="12">
                           <Button
                              title="save"
                              className="shredButton w-100 btn-md"
                              onClick={() => postReceipt()}>
                              SAVE
                           </Button>
                        </Col>
                     </Row>
                  </div>
                  : null
               }


               {/* Enter Net Weight for a Receiving Case */}
               {loaded && rcpt && rcpt.workCase?.barcodeId > 0 ?
                  <Modal isOpen={weight !== false} contentClassName={modalClassName}>
                     <ModalHeader className="mobileBlueBg text-white" children="Enter Net Weight" />
                     <ModalBody className="mobileBlueBgAlt text-white"
                        children={
                           <Table borderless size="sm">
                              <tbody>
                                 <tr>
                                    <td className="data-cell my-0 py-0">
                                       <label className="h6 fw-bold my-0">{rcpt.workCase.itemNumber}</label>
                                    </td>
                                 </tr>
                                 <tr><td className="data-cell m-0 p-0 lineheight8">&nbsp;</td></tr>
                                 <tr>
                                    <td className="data-cell my-0 py-0">
                                       <label className="h6 fw-bold my-0">Net Weight {wgtText}:</label>
                                    </td>
                                 </tr>
                                 <tr>
                                    <td className="data-cell my-0 py-0">
                                       <Input
                                          name="wgt"
                                          type="number"
                                          value={rcpt.workCase.weight}
                                          className="h-100 no-spinner"
                                          onFocus={handleFocus}
                                          onChange={(e) => { onCaseChange("weight", e.target.value) }}
                                          placeholder="Enter Weight ..."
                                       />
                                    </td>
                                 </tr>
                                 <tr><td className="data-cell m-0 p-0 lineheight8">&nbsp;</td></tr>
                                 <tr>
                                    <td className="data-cell my-0 py-3 px-2">
                                       <label className="text-black h6">
                                          <input type="checkbox" checked={rcpt.workCase.weightInKg} className="me-2" onChange={() => toggleKg()} />
                                          Entering Weight in Kg
                                       </label>
                                    </td>
                                 </tr>
                                 <tr>
                                    <td className="data-cell my-0 mx-0 p-0">
                                       <Button
                                          className="shredButton w-100 btn-md my-0"
                                          onClick={() => { addCase(); }}>
                                          OK
                                       </Button>
                                    </td>
                                 </tr>
                                 <tr>
                                    <td className="data-cell my-0 mx-0 p-0">
                                       <Button
                                          className="btn w-100 btn-md mt-2 mb-0"
                                          onClick={() => {
                                             clearCase();
                                          }}>
                                          Cancel
                                       </Button>
                                    </td>
                                 </tr>
                              </tbody>
                           </Table>
                        }
                     />
                  </Modal>
                  : null
               }


               {/* Scan a Barcode */}
               <Modal isOpen={open !== null} contentClassName={modalClassName}>
                  <ModalHeader children="Scan a Barcode" />
                  <ModalBody
                     children={
                        <BarcodeScannerComponent
                           height={350}
                           width={350}
                           onUpdate={(err, result) => {
                              if (result) {
                                 setBarcode(result.text.toUpperCase());
                                 setOpen(null);
                                 setLoaded(false);
                                 getCase();
                              }
                              else {
                                 setBarcode(null);
                              }
                           }}
                           facingMode={"environment"}
                        />
                     }
                  />
                  <ModalFooter>
                     <Button
                        className="shredButton ms-7"
                        onClick={() => {
                           setOpen(null);
                           inputFocus("searchInput");
                        }}>Cancel
                     </Button>
                  </ModalFooter>
               </Modal>


               {/* Override FG Zero Tare Weight */}
               <Modal isOpen={override !== null} contentClassName={modalClassName}>
                  <ModalHeader className="mobileBlueBg text-white" children="Enter Override" />
                  <ModalBody className="mobileBlueBgAlt text-white"
                     children={
                        <div>
                           <label>Supervisor access is required to enter a zero tare weight on this item</label>
                           <div className="input-group">
                              <Input
                                 name="pswd"
                                 type={passwordType}
                                 value={pass}
                                 className="h-100"
                                 onChange={(e) => { setPass(e.target.value) }}
                                 placeholder="Enter Code ..."
                              />
                              <div className="input-group-btn">
                                 <button className="btn btn-md" onClick={togglePassword}>
                                    {passwordType === "password" ? ICONS.shutEye : ICONS.openEye}
                                 </button>
                              </div>
                           </div>
                           {errorMsg ?
                              <label className="text-red">{errorMsg}</label>
                              : null
                           }
                           <br />
                           <Row>
                              <Col xs="12">
                                 <Button className="shredButton w-100 btn-md my-3" onClick={() => { isValidOverride(); }}>
                                    OK
                                 </Button>
                              </Col>
                           </Row>
                           <Row>
                              <Col xs="12">
                                 <Button
                                    className="btn w-100 btn-md my-3"
                                    onClick={() => {
                                       toast.error("Tare weight must be greater than zero");
                                       onChange("allowZeroTare", false);
                                       setZeroTare(false);
                                       setPass("");
                                       setErrorMsg(null);
                                       setOverride(null);
                                       setPasswordType("password");
                                       inputFocus("gross");
                                    }}>
                                    Cancel
                                 </Button>
                              </Col>
                           </Row>
                        </div>
                     }
                  />
               </Modal>


               {/* Override Receiving Weight Total Not Matching Expected Weight */}
               <Modal isOpen={overrideRcpt !== null} contentClassName={modalClassName}>
                  <ModalHeader className="mobileBlueBg text-white" children="Enter Override" />
                  <ModalBody className="mobileBlueBgAlt text-white"
                     children={
                        <div>
                           <label>Supervisor access is required to post this PO</label>
                           <div className="input-group">
                              <Input
                                 name="pswdRcpt"
                                 type={passwordType}
                                 value={pass}
                                 className="h-100"
                                 onFocus={handleFocus}
                                 onChange={(e) => { setPass(e.target.value) }}
                                 placeholder="Enter Code ..."
                              />
                              <div className="input-group-btn">
                                 <button className="btn btn-md" onClick={togglePassword}>
                                    {passwordType === "password" ? ICONS.shutEye : ICONS.openEye}
                                 </button>
                              </div>
                           </div>
                           <br />
                           <Row>
                              <Col xs="12">
                                 <Button
                                    className="shredButton w-100 btn-md my-3"
                                    onClick={() => { isValidRcptOverride(); }}>
                                    OK
                                 </Button>
                              </Col>
                           </Row>
                           <Row>
                              <Col xs="12">
                                 <Button
                                    className="btn w-100 btn-md my-3"
                                    onClick={() => {
                                       toast.error("Total weight must match expected weight");
                                       setPass("");
                                       setOverrideRcpt(null);
                                       setPasswordType("password");
                                       inputFocus("searchInput");
                                    }}>
                                    Cancel
                                 </Button>
                              </Col>
                           </Row>
                        </div>
                     }
                  />
               </Modal>


               {/* Manual Entry of a Barcode */}
               <Modal isOpen={manualEntry !== null} contentClassName={modalClassName}>
                  <ModalHeader children="Enter Barcode" />
                  <ModalBody
                     children={
                        <Input
                           name="manualInput"
                           placeholder="Enter barcode"
                           className="h-100"
                           onChange={(e) => lookup(e.target.value.toUpperCase())}
                           onKeyPress={handleKeypress}
                        />
                     }
                  />
                  <ModalFooter>
                     <ButtonGroup>
                        <Button children="Retrieve" className="button-success" onClick={() => { setManual(null); }} />
                        <Button children="Cancel" className="button-danger" onClick={() => {
                           setBarcode(null);
                           setManual(null);
                           inputFocus("searchInput");
                        }} />
                     </ButtonGroup>
                  </ModalFooter>
               </Modal>

            </ServiceThreadSpinner>
         </DesktopContainer >
      </>
   );
}
